import { useState, useEffect } from 'react';
import { Package2, Trash2, Plus } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';
import { ProductPhotoService } from '../../services/productPhotoService';
import SelectInventoryItemModal from '../manage/SelectInventoryItemModal';
import { createStockTransaction } from '../../lib/stockItems';

interface BookingProduct {
  id: string;
  stock_item_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

interface BookingProductListProps {
  bookingId: string;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  showSalesInfo?: boolean;
  canEdit?: boolean;
}

export default function BookingProductList({
  bookingId,
  onUpdate,
  onError,
  showSalesInfo = true,
  canEdit = true
}: BookingProductListProps) {
  const { selectedBusiness } = useBusiness();
  const [products, setProducts] = useState<BookingProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  const [isSelectInventoryItemModalOpen, setIsSelectInventoryItemModalOpen] = useState(false);

  useEffect(() => {
    if (bookingId) {
      loadProducts();
    }
  }, [bookingId]);


  const loadProducts = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('product_booking_sales')
        .select('*')
        .eq('booking_id', bookingId)
        .eq('business_id', selectedBusiness?.id);

      if (error) {
        console.error('Error loading booking products:', error);
        onError('Failed to load booking products');
        return;
      }

      setProducts(data || []);

      // Load product photos
      if (data && data.length > 0) {
        const stockItemIds = data.map(p => p.stock_item_id);
        try {
          const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
          
          // Convert to the format expected by the component
          const photoUrls: Record<string, string> = {};
          if (photoMap && typeof photoMap === 'object') {
            Object.entries(photoMap).forEach(([itemId, photo]) => {
              if (photo && photo.photo_url) {
                photoUrls[itemId] = photo.photo_url;
              }
            });
          }
          
          setProductPhotos(photoUrls);
        } catch (photoError) {
          console.error('Error loading product photos:', photoError);
          // Continue without photos if photo loading fails
          setProductPhotos({});
        }
      }
    } catch (error) {
      console.error('Error loading booking products:', error);
      onError('Failed to load booking products');
    } finally {
      setLoading(false);
    }
  };


  const handleRemoveProduct = async (productId: string, stockItemId: string, quantity: number) => {
    try {
      // Remove from product_booking_sales table
      const { error: deleteError } = await supabase
        .from('product_booking_sales')
        .delete()
        .eq('id', productId);

      if (deleteError) {
        console.error('Error removing product from booking:', deleteError);
        onError('Failed to remove product from booking');
        return;
      }

      // Find and remove the specific stock transaction for this booking and item
      const { error: stockError } = await supabase
        .from('stock_transactions')
        .delete()
        .eq('booking_id', bookingId)
        .eq('item_id', stockItemId)
        .eq('type', 'sale');

      if (stockError) {
        console.error('Error removing stock transaction:', stockError);
        onError('Product removed from booking but stock transaction not removed');
      }

      // Reload products and trigger booking update
      await loadProducts();
      await onUpdate();
      
    } catch (error) {
      console.error('Error removing product:', error);
      onError('Failed to remove product');
    }
  };

  const handleAddProduct = async (product: any) => {
    try {
      // Create stock transaction to reduce inventory
      await createStockTransaction({
        item_id: product.id,
        type: 'sale',
        quantity: 1, // Default quantity of 1
        price: product.acquisition_cost,
        notes: `Added to booking: ${bookingId}`,
        booking_id: bookingId
      });

      // Create product_booking_sales entry
      const { error: productSaleError } = await supabase
        .from('product_booking_sales')
        .insert({
          business_id: selectedBusiness?.id,
          booking_id: bookingId,
          stock_item_id: product.id,
          product_name: product.name,
          quantity: 1,
          unit_price: product.price,
          total_price: product.price * 1
        });

      if (productSaleError) {
        console.error('Error adding product to booking:', productSaleError);
        onError('Failed to add product to booking');
        return;
      }

      // Close modal first to prevent flashing
      setIsSelectInventoryItemModalOpen(false);
      
      // Small delay to ensure modal closes smoothly before updates
      setTimeout(async () => {
        // Reload products and trigger booking update
        await loadProducts();
        await onUpdate();
      }, 100);
      
    } catch (error) {
      console.error('Error adding product:', error);
      onError('Failed to add product');
    }
  };

  const ProductIcon = ({ productId, productName }: { productId: string; productName: string }) => {
    const photoUrl = productPhotos[productId];
    
    if (photoUrl) {
      return (
        <div className="h-14 w-14 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
          <img 
            src={photoUrl} 
            alt={productName}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, show fallback icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = '<div class="h-6 w-6 text-blue-600"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29,7 12,12 20.71,7"/><line x1="12" x2="12" y1="22" y2="12"/></svg></div>';
                parent.className = "h-14 w-14 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0";
              }
            }}
          />
        </div>
      );
    }
    
    return (
      <div className="h-14 w-14 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
        <Package2 className="h-6 w-6" />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="p-4">
        {/* Minimal Add Product Button */}
        {canEdit && (
          <button
            onClick={() => setIsSelectInventoryItemModalOpen(true)}
            className="w-full flex items-center justify-center px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-800 hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </button>
        )}

        {/* Select Inventory Item Modal */}
        <SelectInventoryItemModal
          isOpen={isSelectInventoryItemModalOpen}
          onClose={() => setIsSelectInventoryItemModalOpen(false)}
          onAdd={handleAddProduct}
          currentBookingProducts={products.map(p => ({
            product: {
              id: p.stock_item_id,
              name: p.product_name,
              quantity: p.quantity
            }
          }))}
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="space-y-3">
        {products.map((product) => (
          <div key={product.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <ProductIcon productId={product.stock_item_id} productName={product.product_name} />
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">{product.product_name}</h4>
                <p className="text-sm text-gray-500">
                  Quantity: {product.quantity} × ${product.unit_price.toFixed(2)}
                </p>
                {canEdit && (
                  <button
                    onClick={() => handleRemoveProduct(product.id, product.stock_item_id, product.quantity)}
                    className="flex items-center space-x-1 text-xs text-red-600 hover:text-red-800 mt-1 transition-colors"
                  >
                    <Trash2 className="h-3 w-3" />
                    <span>Remove</span>
                  </button>
                )}
              </div>
            </div>
            
            {showSalesInfo && (
              <div className="text-right">
                <div className="font-semibold text-gray-900">
                  ${product.total_price.toFixed(2)}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Product Button */}
      {canEdit && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <button
            onClick={() => setIsSelectInventoryItemModalOpen(true)}
            className="w-full flex items-center justify-center px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-800 hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </button>
        </div>
      )}

      {/* Select Inventory Item Modal */}
      <SelectInventoryItemModal
        isOpen={isSelectInventoryItemModalOpen}
        onClose={() => setIsSelectInventoryItemModalOpen(false)}
        onAdd={handleAddProduct}
        currentBookingProducts={products.map(p => ({
          product: {
            id: p.stock_item_id,
            name: p.product_name,
            quantity: p.quantity
          }
        }))}
      />
    </div>
  );
}
